import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="refresh"
export default class extends Controller {
	static targets = ["timer", "countdown", "exhausted"];
	// full: reload via window.location instead of Turbo.visit — required when the
	// revisited URL may redirect to another origin (CSP blocks Turbo's fetch).
	// max: how many refreshes this page is allowed before it gives up and says so
	// (0, the default, polls forever — the subscription/Pix pages still do).
	static values = {
		timer: { type: Number, default: 10 },
		full: { type: Boolean, default: false },
		max: { type: Number, default: 0 },
	};

	connect() {
		this.initialTimer = this.timerValue;
		this.timerTargets.forEach((target) => {
			target.textContent = this.timerValue;
		});

		// The budget cannot live in memory: every refresh re-renders this page and
		// re-runs connect(). It rides sessionStorage under this URL's path, so it
		// belongs to the TAB: reloading here shows the current server state and
		// then stops again straight away, while a new tab on the same page starts
		// a fresh budget. Where sessionStorage is unavailable (private mode,
		// blocked cookies) the page simply polls as it used to.
		if (this.#spent()) {
			this.#giveUp();
			return;
		}

		this.start();
	}

	disconnect() {
		this.stop();
	}

	start() {
		this.timer = setInterval(() => {
			this.timerValue = Math.max(0, this.timerValue - 1); // Prevent negative values
			this.timerTargets.forEach((target) => {
				target.textContent = this.timerValue;
			});

			if (this.timerValue <= 0) {
				// Restart the countdown so a visit that fails to replace the page
				// (e.g. offline) retries at the normal cadence, not every second.
				this.timerValue = this.initialTimer;
				this.refresh();
			}
		}, 1000);
	}

	stop() {
		clearInterval(this.timer);
	}

	refresh() {
		if (this.#spent()) {
			this.#giveUp();
			return;
		}

		this.#recordPoll();
		if (this.fullValue) {
			window.location.reload();
		} else {
			Turbo.visit(window.location.href);
		}
	}

	#spent() {
		return this.maxValue > 0 && this.#polls() >= this.maxValue;
	}

	// Stops asking and swaps the countdown for whatever the page says instead —
	// on the order conclusion, that we will e-mail. Reloading still works, and
	// that is the point: the page stops promising to check for the customer, not
	// the other way round.
	#giveUp() {
		this.stop();
		this.countdownTargets.forEach((target) => {
			target.hidden = true;
		});
		this.exhaustedTargets.forEach((target) => {
			target.hidden = false;
		});
	}

	#storageKey() {
		return `refresh-polls:${window.location.pathname}`;
	}

	#polls() {
		try {
			return Number.parseInt(window.sessionStorage.getItem(this.#storageKey()), 10) || 0;
		} catch {
			return 0;
		}
	}

	#recordPoll() {
		if (this.maxValue <= 0) return;

		try {
			window.sessionStorage.setItem(this.#storageKey(), String(this.#polls() + 1));
		} catch {
			// No sessionStorage (private mode, blocked cookies): the bound does not
			// hold, and this page polls the way it always did.
		}
	}
}
